/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import CloseIcon from "@mui/icons-material/Close";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import useApi from "components/Utils/api";
import toast from "utils/toast";
import { useMaterialUIController } from "context";
import Papa from "papaparse";

const filterFieldSx = { minWidth: "220px", "& .MuiOutlinedInput-root": { height: "38px" } };
const filterSelectSx = {
  minWidth: "220px",
  "& .MuiOutlinedInput-root": { height: "38px" },
  "& .MuiSelect-select": { fontSize: "0.8rem" },
};
const filterLabelProps = { shrink: true, style: { fontSize: "0.8rem" } };
const filterInputProps = { style: { fontSize: "0.8rem" } };

const INITIAL_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  address: "",
  active: true,
};

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const formatCreatedAt = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = d.getDate();
  const month = MONTH_NAMES[d.getMonth()];
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const seconds = String(d.getSeconds()).padStart(2, "0");
  return `${day} ${month} ${year} ${hours}:${minutes}:${seconds}`;
};

function ActiveBadge({ value }) {
  const isActive = value === "ACTIVE" || value === true || value === 1 || value === "1";
  return (
    <MDBox
      sx={{
        display: "inline-block",
        px: 1.5,
        py: 0.4,
        borderRadius: "8px",
        backgroundColor: isActive ? "#d4edda" : "#f8d7da",
        color: isActive ? "#155724" : "#721c24",
        fontWeight: "600",
        fontSize: "0.72rem",
        whiteSpace: "nowrap",
      }}
    >
      {isActive ? "Active" : "Inactive"}
    </MDBox>
  );
}

function CustomerDialog({ open, onClose, onSave, onDelete, loading, editCustomer }) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    if (editCustomer) {
      setForm({
        firstName: editCustomer.firstName || "",
        lastName: editCustomer.lastName || "",
        email: editCustomer.email || "",
        phoneNumber: editCustomer.phoneNumber || "",
        address: editCustomer.address || "",
        active: editCustomer.status === "ACTIVE" || editCustomer.active === 1 || editCustomer.active === true || editCustomer.active === "1",
      });
    } else {
      setForm(INITIAL_FORM);
    }
    setErrors({});
  }, [open, editCustomer]);

  const set = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: false }));
  };

  const validate = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const newErrors = {
      firstName: !form.firstName.trim(),
      lastName: !form.lastName.trim(),
      email: !form.email.trim() || !emailRegex.test(form.email.trim()),
      phoneNumber: !form.phoneNumber.trim(),
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave(form);
  };

  const inputSx = { mb: 2 };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(0,0,0,0.1)",
          py: 1.5,
        }}
      >
        <MDTypography variant="h6" fontWeight="medium">
          {editCustomer ? "Edit Customer" : "New Customer"}
        </MDTypography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="First Name"
              fullWidth
              size="small"
              required
              value={form.firstName}
              onChange={set("firstName")}
              error={!!errors.firstName}
              helperText={errors.firstName ? "First name is required" : ""}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Last Name"
              fullWidth
              size="small"
              required
              value={form.lastName}
              onChange={set("lastName")}
              error={!!errors.lastName}
              helperText={errors.lastName ? "Last name is required" : ""}
              sx={inputSx}
            />
          </Grid>
        </Grid>

        <TextField
          label="Email"
          fullWidth
          size="small"
          required
          value={form.email}
          onChange={set("email")}
          error={!!errors.email}
          helperText={errors.email ? "Valid email is required" : ""}
          sx={inputSx}
        />

        <TextField
          label="Phone Number"
          fullWidth
          size="small"
          required
          value={form.phoneNumber}
          onChange={set("phoneNumber")}
          error={!!errors.phoneNumber}
          helperText={errors.phoneNumber ? "Phone number is required" : ""}
          sx={inputSx}
        />

        <TextField
          label="Address"
          fullWidth
          size="small"
          multiline
          rows={2}
          value={form.address}
          onChange={set("address")}
          sx={inputSx}
        />

        <FormControlLabel
          control={
            <Switch
              checked={form.active}
              onChange={(e) => setForm((prev) => ({ ...prev, active: e.target.checked }))}
              size="small"
            />
          }
          label={
            <MDTypography variant="button" fontWeight="regular" sx={{ textTransform: "none" }}>
              Active
            </MDTypography>
          }
        />

        {editCustomer && (
          <MDBox display="flex" gap={3} mt={1}>
            <MDTypography variant="caption" color="text">
              Created At: {formatCreatedAt(editCustomer.createdAt)}
            </MDTypography>
            <MDTypography variant="caption" color="text">
              Modified At: {formatCreatedAt(editCustomer.updatedAt)}
            </MDTypography>
          </MDBox>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid rgba(0,0,0,0.1)", gap: 1 }}>
        <MDButton variant="outlined" color="secondary" onClick={onClose} disabled={loading}>
          Cancel
        </MDButton>
        {editCustomer && (
          <MDButton variant="outlined" color="error" onClick={() => onDelete(editCustomer.customerId ?? editCustomer.id)} disabled={loading}>
            Delete
          </MDButton>
        )}
        <MDBox flex={1} />
        <MDButton variant="gradient" color="success" onClick={handleSave} disabled={loading}>
          {loading ? "Saving…" : editCustomer ? "Update" : "Create"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

function Customers() {
  const [controller] = useMaterialUIController();
  const mid = controller.merchant?.mid;
  const { sidenavColor } = controller;

  const [rows, setRows] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const api = useApi();

  const isRowActive = (row) =>
    row.status === "ACTIVE" || row.active === true || row.active === 1 || row.active === "1";

  const filteredRows = rows.filter((row) => {
    const matchesStatus =
      statusFilter === "All" || (statusFilter === "Active" ? isRowActive(row) : !isRowActive(row));

    const term = search.trim().toLowerCase();
    const matchesSearch =
      !term ||
      [row.firstName, row.lastName, row.email, row.phoneNumber]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(term));

    return matchesStatus && matchesSearch;
  });

  const columns = [
    { Header: "ID", accessor: "customerId", width: "6%", align: "left" },
    { Header: "First Name", accessor: "firstName", width: "12%", align: "left" },
    { Header: "Last Name", accessor: "lastName", width: "12%", align: "left" },
    { Header: "Email", accessor: "email", width: "18%", align: "left" },
    { Header: "Phone Number", accessor: "phoneNumber", width: "12%", align: "left" },
    {
      Header: "Status",
      accessor: "status",
      width: "8%",
      align: "left",
      Cell: ({ value }) => <ActiveBadge value={value} />,
    },
    {
      Header: "Created At",
      accessor: "createdAt",
      width: "14%",
      align: "left",
      Cell: ({ value }) => formatCreatedAt(value),
    },
    {
      Header: "Modified At",
      accessor: "updatedAt",
      width: "14%",
      align: "left",
      Cell: ({ value }) => formatCreatedAt(value),
    },
  ];

  const fetchData = () => {
    api
      .get("customers")
      .then((res) => setRows(res.data?.data ?? []))
      .catch((error) => {
        if (error.response?.status === 403) {
          toast.error("You do not have permission to perform this action.");
          return;
        }
        setRows([]);
      });
  };

  const handleDownloadCSV = () => {
    if (rows.length === 0) {
      toast.error("No records found to download");
      return;
    }
    const headers = columns.map((c) => c.Header);
    const values = rows.map((row) =>
      columns.map((c) =>
        typeof c.accessor === "function" ? c.accessor(row) : row[c.accessor]
      )
    );
    const csv = Papa.unparse([headers, ...values], { quotes: true });
    const ts = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Customers_${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleSave = (form) => {
    const isEdit = !!selectedCustomer;

    setSaving(true);

    if (!isEdit) {
      const body = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phoneNumber: form.phoneNumber,
        address: form.address,
      };

      api
        .post("customers", body)
        .then(() => {
          toast.success("Customer created.");
          setDialogOpen(false);
          setSelectedCustomer(null);
          fetchData();
        })
        .catch((error) => {
          const status = error.response?.status;
          const resData = error.response?.data;
          if (status === 400) {
            if (Array.isArray(resData?.errors) && resData.errors.length > 0) {
              toast.error(resData.errors.join("\n"));
            } else if (resData?.message) {
              toast.error(resData.message);
            } else {
              toast.error("Failed to create customer.");
            }
          } else if (status === 401) {
            toast.error(resData?.message ?? "Unauthorized. Please log in again.");
          } else if (status === 403) {
            toast.error("You do not have permission to perform this action.");
          } else {
            toast.error("Failed to create customer.");
          }
        })
        .finally(() => setSaving(false));
    } else {
      const customerId = selectedCustomer.customerId ?? selectedCustomer.id;
      const newStatus = form.active ? "ACTIVE" : "INACTIVE";
      const statusChanged = newStatus !== selectedCustomer.status;

      const putBody = {
        customerId,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phoneNumber: form.phoneNumber,
        address: form.address,
      };

      const handlePutError = (error) => {
        const status = error.response?.status;
        const resData = error.response?.data;
        if (status === 400) {
          if (Array.isArray(resData?.errors) && resData.errors.length > 0) {
            toast.error(resData.errors.join("\n"));
          } else if (resData?.message) {
            toast.error(resData.message);
          } else {
            toast.error("Failed to update customer.");
          }
        } else if (status === 404) {
          toast.error(resData?.message ?? "Customer not found.");
        } else if (status === 403) {
          toast.error("You do not have permission to perform this action.");
        } else {
          toast.error("Failed to update customer.");
        }
      };

      const handleStatusError = (error) => {
        const status = error.response?.status;
        const resData = error.response?.data;
        if (status === 400) {
          if (Array.isArray(resData?.errors) && resData.errors.length > 0) {
            toast.error(resData.errors.join("\n"));
          } else if (resData?.message) {
            toast.error(resData.message);
          } else {
            toast.error("Failed to update customer status.");
          }
        } else if (status === 404) {
          toast.error(resData?.message ?? "Customer not found.");
        } else if (status === 403) {
          toast.error("You do not have permission to perform this action.");
        } else {
          toast.error("Failed to update customer status.");
        }
      };

      api
        .put("customers", putBody)
        .then(() => {
          if (!statusChanged) return Promise.resolve(null);
          return api.patch("customers/status", { customerId, status: newStatus });
        })
        .then(() => {
          toast.success("Customer updated.");
          setDialogOpen(false);
          setSelectedCustomer(null);
          fetchData();
        })
        .catch((error) => {
          const url = error.config?.url ?? "";
          if (url.includes("status")) {
            handleStatusError(error);
          } else {
            handlePutError(error);
          }
        })
        .finally(() => setSaving(false));
    }
  };

  const handleDelete = (id) => {
    setSaving(true);
    api
      .delete(`customers/${id}`)
      .then(() => {
        toast.success("Customer deleted.");
        setDialogOpen(false);
        setSelectedCustomer(null);
        fetchData();
      })
      .catch((error) => {
        const status = error.response?.status;
        const resData = error.response?.data;
        if (status === 404) {
          toast.error(resData?.message ?? "Customer not found.");
        } else if (status === 401) {
          toast.error(resData?.message ?? "Unauthorized. Please log in again.");
        } else if (status === 403) {
          toast.error("You do not have permission to perform this action.");
        } else {
          toast.error("Failed to delete customer.");
        }
      })
      .finally(() => setSaving(false));
  };

  const openNew = () => {
    setSelectedCustomer(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedCustomer(null);
  };

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={3} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={12}>
            <MDBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
              <MDBox>
                <MDTypography variant="h5" fontWeight="bold" sx={{ lineHeight: 1.2, mb: 0.25 }}>
                  Customers
                </MDTypography>
                <MDTypography variant="button" color="text" fontWeight="regular" sx={{ lineHeight: 1 }}>
                  {rows.length} customer{rows.length === 1 ? "" : "s"}
                </MDTypography>
              </MDBox>
              <MDBox display="flex" gap={1}>
                <MDButton variant="outlined" color="secondary" size="small" onClick={handleDownloadCSV}>
                  <Icon sx={{ mr: 0.5 }} fontSize="small">download</Icon>
                  Export
                </MDButton>
                <MDButton variant="gradient" color={sidenavColor} size="small" onClick={openNew}>
                  <Icon sx={{ mr: 0.5 }} fontSize="small">add</Icon>
                  New customer
                </MDButton>
              </MDBox>
            </MDBox>

            <Card
              sx={({ boxShadows }) => ({
                border: "1px solid",
                borderColor: "grey.300",
                boxShadow: boxShadows.sm,
                borderRadius: "12px",
                overflow: "hidden",
              })}
            >
              <MDBox
                display="flex"
                flexWrap="wrap"
                gap={2}
                px={2.25}
                py={1.25}
                bgColor="grey-100"
                sx={{ borderBottom: "1px solid", borderColor: "grey.300" }}
              >
                <TextField
                  label="Search"
                  placeholder="Name, email or phone"
                  size="small"
                  variant="outlined"
                  InputLabelProps={filterLabelProps}
                  InputProps={filterInputProps}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={filterFieldSx}
                />
                <TextField
                  select
                  label="Status"
                  size="small"
                  variant="outlined"
                  InputLabelProps={filterLabelProps}
                  InputProps={filterInputProps}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={filterSelectSx}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </TextField>
              </MDBox>
              <DataTable
                table={{ columns, rows: filteredRows }}
                showTotalEntries
                isSorted
                noEndBorder
                hideExpandColumn
                entriesPerPage
                entriesPerPagePosition="bottom"
                disableRowDialog
                onRowClicked={(row) => {
                  setSelectedCustomer(row.original);
                  setDialogOpen(true);
                }}
              />
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <CustomerDialog
        open={dialogOpen}
        onClose={closeDialog}
        onSave={handleSave}
        onDelete={handleDelete}
        loading={saving}
        editCustomer={selectedCustomer}
      />
    </DashboardLayout>
  );
}

export default Customers;
